// modules
import { useCallback, useState } from "react";
import { Form } from "antd";
import { useNavigate } from "react-router-dom";

// service calls

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// constants and types
import { CR_MEMBERSHIP_STATUS as Constants } from "constants/global";
import { FormInstance } from "antd/es/form/Form";
import { crUpdateMembershipStatus } from "services/api/crAccount";

type CRMembershipStatusPropTypes = {
	proceedToSuspendCRMembershipStatus: () => void;
	proceedToUnsuspendCRMembershipStatus: () => void;
	loading: boolean;
	form: FormInstance;
	externalId: string;
	remarks: string;
	handleExternalId: (e: { target: { id: any; value: any } }) => void;
	handleRemarks: (e: { target: { id: any; value: any } }) => void;
};

export function useCRMembershipStatusPage(): CRMembershipStatusPropTypes {
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const [loading, setLoading] = useState<boolean>(false);
	const [externalId, setExternalId] = useState<string>("");
	const [remarks, setRemarks] = useState<string>("");

	const onSuccess = useCallback(() => {
		navigate(0);
	}, [navigate]);

	const showSuccessCountDownModal = useCallback(
		(data: any) => {
			CountDownModal({
				timer: 10,
				errorIcon: false,
				title: Constants.SUCCESS,
				subtitle: data,
				onTimeComplete: onSuccess
			});
		},
		[loading]
	);

	const onFailure = useCallback(
		(error: any) => {
			setLoading(false);
			CountDownModal({
				closable: true,
				timer: 10,
				errorIcon: true,
				title: Constants.SERVER_ERR,
				subtitle: error?.message,
				onTimeComplete: () => {
					return false;
				}
			});
		},
		[loading]
	);

	const handleFailure = useCallback(
		(errors: unknown) => {
			if (Array.isArray(errors) && errors.length > 0) {
				onFailure(errors[0]);
			} else {
				onFailure({ message: Constants.SERVER_ERR });
			}
		},
		[loading]
	);

	const handleExternalId = useCallback(
		(e: { target: { id: any; value: any } }) => {
			setExternalId(e?.target?.value);
		},
		[externalId]
	);

	const handleRemarks = useCallback(
		(e: { target: { id: any; value: any } }) => {
			setRemarks(e?.target?.value);
		},
		[remarks]
	);

	const proceedToSuspendCRMembershipStatus = useCallback(async () => {
		if (externalId) {
			setLoading(true);
			await crUpdateMembershipStatus({ external_id: externalId, new_status: "SUSPENDED", remarks: remarks })
				.then((response) => {
					showSuccessCountDownModal(response?.data?.data);
				})
				.catch((error) => {
					handleFailure(error?.response?.data?.errors);
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [externalId, remarks, loading]);

	const proceedToUnsuspendCRMembershipStatus = useCallback(async () => {
		if (externalId) {
			setLoading(true);
			await crUpdateMembershipStatus({ external_id: externalId, new_status: "ACTIVE", remarks: remarks })
				.then((response) => {
					showSuccessCountDownModal(response?.data?.data);
				})
				.catch((error) => {
					handleFailure(error?.response?.data?.errors);
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [externalId, remarks, loading]);

	return {
		proceedToSuspendCRMembershipStatus,
		proceedToUnsuspendCRMembershipStatus,
		loading,
		form,
		externalId,
		remarks,
		handleExternalId,
		handleRemarks
	};
}
